import gql from 'graphql-tag';

const FRAGMENT_ALL_BOOKING_GUEST_FIELDS = gql`
  fragment FRAGMENT_ALL_BOOKING_GUEST_FIELDS on BookingGuest {
    id
    identityId
    assigned
    firstName
    lastName
    supervisor
    phone
    emailAddress
    referenceNumber
    room {
      id
      description
      name
      guestsPerRoom
    }
    roomIndex
    guestDays {
      date
      inboundRate {
        value
        formatted
        name
      }
      status
    }
    nightShift
    company
    position
    complete
    comments
  }
`;
const FRAGMENT_ALL_BOOKING_FIELDS = gql`
  fragment FRAGMENT_ALL_BOOKING_FIELDS on Booking {
    id
    bookingNumber
    bookingCategory
    bookingStatus
    connected
    isLNROverrideActive
    confirmationNumber
    confirmationMessage
    bookingCategory
    cancellationStatus {
      cancellationMessage
      cancelledBy
    }
    cancellationMessage
    cancellationDate
    paymentCode {
      id
      code
      description
      organization {
        id
        slug
        name
      }
    }
    cardSource {
      source
      cardNickname
      cardholderName
      lastFourDigits
      expiry
      address
      unitNumber
      city
      province
      postalCode
    }
    owner {
      id
      emailAddress
      firstName
      lastName
    }
    createdBy {
      id
      emailAddress
    }
    checkin
    checkout
    roomCount
    guestCount
    maxGuest
    crewSwapOccupancy
    maxDays
    permissions
    property {
      id
      slug
      countryCode
      name
      standBy
      connected
      address {
        city
        region
        country
        address
        postalCode
      }
      cardImage {
        id
        href
      }
      geo {
        lat
        lng
      }
      timeZone
      rooms {
        id
        description
        name
        active
        rate {
          symbol
          name
          value
          formatted
        }
        roomType {
          id
        }
        guestsPerRoom
        availableRooms
      }
    }
    estimatedTotal {
      formatted
      name
      value
      symbol
      name
    }
    actualTotal {
      formatted
      name
    }
    estimatedTaxesFees {
      formatted
      name
      value
    }
    actualTaxesFees {
      formatted
      name
      value
    }
    summaryByRoomType {
      id
      numberOfNights
      numberOfRooms
      roomName
      roomTypePrice {
        value
        name
        formatted
      }
      dates {
        date
        breakdown {
          totalRooms
          bookedRooms
          bookedRate {
            value
            formatted
            name
          }
          standByRate {
            value
            formatted
            name
          }
          standByRooms
          allRatesForThisDate
        }
      }
    }
    guests {
      id
      identityId
      assigned
      firstName
      lastName
      supervisor
      phone
      emailAddress
      employeeId
      referenceNumber
      nightShift
      company
      position
      complete
      comments
      guestDays {
        date
        status
        costTrackingCodeId
        costTrackingCode {
          code
          id
          active
        }
        state
        outboundRate {
          value
        }
        taxes {
          value
        }
        fees {
          value
        }
      }
      roomIndex
      room {
        id
        name
        description
        roomType {
          id
        }
      }
    }
    specialRequests
    paymentMethod
    connected
    travelRequestType
    bookingOrigin
    previousBookingId
  }
`;
export const QUERY_GET_BOOKING = gql`
  query QUERY_GET_BOOKING($bookingNumber: String, $draftEdit: Boolean) {
    getBookingByBookingNumber(bookingNumber: $bookingNumber, draftEdit: $draftEdit) {
      ...FRAGMENT_ALL_BOOKING_FIELDS
      canBeConnected
      bookingBypassReason {
        bypassReason
      }
    }
  }
  ${FRAGMENT_ALL_BOOKING_FIELDS}
`;
export const QUERY_PROPERTY_ROOMS = gql`
  query QUERY_PROPERTY_ROOMS($slug: String!, $dates: CheckinCheckout, $skip: Boolean!) {
    property(slug: $slug, dates: $dates) @skip(if: $skip) {
      id
      callForPricing
      rooms {
        id
        rate {
          symbol
          position
          value
          formatted
          name
        }
        strikeoutRate {
          name
          formatted
          value
        }
        availability # AVAILABLE, LIMITED, SOLD_OUT
        numRoomsAvail
        nightlyRates {
          date
          price
        }
        averageRateAfterCalculations {
          symbol
          position
          value
          formatted
          name
        }
      }
    }
  }
`;
export const QUERY_GET_USER_PAYMENT_CODES = gql`
  query QUERY_GET_USER_PAYMENT_CODES {
    getUserPaymentCodes {
      id
      code
      description
      expiryDate
      organization {
        id
        name
      }
    }
  }
`;
export const QUERY_GET_ORGANIZATION_COST_TRACKING_CODES = gql`
  query QUERY_GET_ORGANIZATION_COST_TRACKING_CODES {
    getOrganizationCostTrackingCodes {
      id
      code
      description
      expiryDate
      organization {
        id
        name
      }
    }
  }
`;
export const QUERY_GET_IDENTITY_FOR_EMAIL = gql`
  query QUERY_GET_IDENTITY_FOR_EMAIL($emailAddress: EmailAddress!) {
    getIdentityForEmail(emailAddress: $emailAddress) {
      id
      firstName
      lastName
      company
      position
      phone
      emailAddress
    }
  }
`;
export const MUTATION_CALCULATE_BOOKING_PRICE_FROM_FULL_BOOKING = gql`
  mutation MUTATION_CALCULATE_BOOKING_PRICE_FROM_FULL_BOOKING($input: CalculateBookingPriceInput!) {
    calculateBookingPriceFromFullBooking(input: $input) {
      total {
        value
        name
        formatted
      }
      taxesAndFees {
        value
        name
        formatted
      }
      subtotal {
        value
        name
        formatted
      }
      daysWithRates
    }
  }
`;
export const MUTATION_CALCULATE_RATES_FOR_ROOM_SCHEDULE_EDIT = gql`
  mutation MUTATION_CALCULATE_RATES_FOR_ROOM_SCHEDULE_EDIT($input: CalculateBookingPriceInput!) {
    calculateRoomScheduleBookingEditRates(input: $input) {
      total {
        value
        name
        formatted
      }
      taxesAndFees {
        value
        name
        formatted
      }
      subtotal {
        value
        name
        formatted
      }
      daysWithRates
    }
  }
`;

export const MUTATION_CALCULATE_RATES_FOR_ROOM_SCHEDULE_INITIAL = gql`
  mutation MUTATION_CALCULATE_RATES_FOR_ROOM_SCHEDULE_INITIAL($input: CalculateBookingPriceInput!) {
    calculateRoomScheduleBookingInitialRates(input: $input) {
      total {
        value
        name
        formatted
      }
      taxesAndFees {
        value
        name
        formatted
      }
      subtotal {
        value
        name
        formatted
      }
      daysWithRates
    }
  }
`;

export const MUTATION_INITIATE_BOOKING = gql`
  mutation MUTATION_INITIATE_BOOKING(
    $propertyId: Int!
    $checkin: LodgeLinkDate!
    $checkout: LodgeLinkDate!
    $rooms: [BookingRoomInput!]!
    $bookingBypassReasonId: Int
    $bookingCategory: BookingCategory!
  ) {
    initiateBooking(
      booking: {
        propertyId: $propertyId
        checkin: $checkin
        checkout: $checkout
        rooms: $rooms
        bookingBypassReasonId: $bookingBypassReasonId
        bookingCategory: $bookingCategory
      }
    ) {
      message
      booking {
        ...FRAGMENT_ALL_BOOKING_FIELDS
      }
    }
  }
  ${FRAGMENT_ALL_BOOKING_FIELDS}
`;

export const MUTATION_ADD_CREW_MEMBERS_TO_BOOKING_ROOM = gql`
  mutation MUTATION_ADD_CREW_MEMBERS_TO_BOOKING_ROOM(
    $bookingId: Int!
    $crewMembers: [AddCrewMemberToBookingInput!]
    $roomId: Int!
    $roomIndex: Int
  ) {
    addCrewMembersToBookingRoom(
      bookingId: $bookingId
      crewMembers: $crewMembers
      roomId: $roomId
      roomIndex: $roomIndex
    ) {
      message
      booking {
        ...FRAGMENT_ALL_BOOKING_FIELDS
      }
    }
  }
  ${FRAGMENT_ALL_BOOKING_FIELDS}
`;

export const MUTATION_EDIT_BOOKING_GUEST = gql`
  mutation MUTATION_EDIT_BOOKING_GUEST(
    $bookingId: Int!
    $guestInfo: BookingGuestInput!
    $sendNotifications: Boolean
    $sendGDSUpdate: Boolean
  ) {
    editGuestForBooking(
      bookingId: $bookingId
      guestInfo: $guestInfo
      sendNotifications: $sendNotifications
      sendGDSUpdate: $sendGDSUpdate
    ) {
      message
      guest {
        ...FRAGMENT_ALL_BOOKING_GUEST_FIELDS
      }
      permissions
    }
  }
  ${FRAGMENT_ALL_BOOKING_GUEST_FIELDS}
`;

export const MUTATION_SET_COST_TRACKING_CODES_FOR_BOOKING = gql`
  mutation MUTATION_SET_COST_TRACKING_CODES_FOR_BOOKING(
    $bookingId: Int!
    $costTrackingCodes: [BookingCostTrackingCodeInput!]
  ) {
    setCostTrackingCodesForBooking(bookingId: $bookingId, costTrackingCodes: $costTrackingCodes) {
      message
    }
  }
`;

export const MUTATION_UPDATE_BOOKING_TRAVEL_REQUEST = gql`
  mutation MUTATION_UPDATE_BOOKING_TRAVEL_REQUEST($bookingId: Int!, $travelRequestType: TravelRequestType) {
    updateBookingTravelRequest(bookingId: $bookingId, travelRequestType: $travelRequestType) {
      message
    }
  }
`;
export const MUTATION_CANCEL_BOOKING = gql`
  mutation MUTATION_CANCEL_BOOKING(
    $bookingId: Int!
    $preferences: EmailPreferenceInput!
    $bookingFeeEvent: BookingFeeEventInput
  ) {
    cancelBooking(bookingId: $bookingId, preferences: $preferences, bookingFeeEvent: $bookingFeeEvent) {
      message
      booking {
        ...FRAGMENT_ALL_BOOKING_FIELDS
      }
    }
  }
  ${FRAGMENT_ALL_BOOKING_FIELDS}
`;
export const MUTATION_MODIFY_SPECIAL_REQUEST = gql`
  mutation MUTATION_MODIFY_SPECIAL_REQUEST($bookingId: Int!, $specialRequests: String!) {
    makeSpecialRequest(bookingId: $bookingId, specialRequests: $specialRequests) {
      message
      booking {
        ...FRAGMENT_ALL_BOOKING_FIELDS
      }
    }
  }
  ${FRAGMENT_ALL_BOOKING_FIELDS}
`;
export const MUTATION_START_EDIT = gql`
  mutation MUTATION_START_EDIT($bookingNumber: String!) {
    startBookingEdit(bookingNumber: $bookingNumber) {
      message
      booking {
        ...FRAGMENT_ALL_BOOKING_FIELDS
      }
    }
  }
  ${FRAGMENT_ALL_BOOKING_FIELDS}
`;

export const MUTATION_CHANGE_CHECKIN_CHECKOUT_DATE = gql`
  ${FRAGMENT_ALL_BOOKING_FIELDS}
  mutation MUTATION_CHANGE_CHECKIN_CHECKOUT_DATE(
    $bookingId: Int!
    $guestIds: [Int!]
    $checkinDate: String
    $checkoutDate: String
  ) {
    changeCheckinCheckoutDateEdit(
      bookingId: $bookingId
      guestIds: $guestIds
      checkinDate: $checkinDate
      checkoutDate: $checkoutDate
    ) {
      message
      booking {
        ...FRAGMENT_ALL_BOOKING_FIELDS
      }
    }
  }
`;
export const MUTATION_SUBMIT_BOOKING = gql`
  mutation MUTATION_SUBMIT_BOOKING($booking: BookingInput!, $isInitialFlow: Boolean) {
    submitBooking(booking: $booking, isInitialFlow: $isInitialFlow) {
      message
      booking {
        ...FRAGMENT_ALL_BOOKING_FIELDS
      }
    }
  }
  ${FRAGMENT_ALL_BOOKING_FIELDS}
`;
